import { DateTime } from "luxon";
import { styled } from "styled-components";
import { memo, useMemo, useRef, useState } from "react";
import { Icon, Text, useParsedSearchParams } from "@fidelix/fx-miranda";
import { isEqual } from "lodash-es";
import { ParsedPointId } from "@fidelix/fx-margaret";
import { Placement } from "react-aria";
import { Point, PointType } from "../../point/point.model";
import { useGraphicsContext } from "../graphics-context";
import { useGraphicsHistoryQuery } from "../graphics.queries";
import { getAlarmTimelineEvents, TimelineEvents } from "./history-replay.utils";
import {
  HistoryReplayContextProps,
  useHistoryReplayContext,
} from "./history-replay-context";
import { convertValueFromOldRangeToNewRange } from "../../chart/chart.utils";
import { EditMode, SetValuePopover } from "../set-value-popover";

const SCATTER_SIZE = 20;

export function HistoryTimelineEvents() {
  const { parsedParams } = useParsedSearchParams({
    to: { type: "string", defaultValue: undefined },
    from: { type: "string", defaultValue: undefined },
  });
  const { pointsQuery } = useGraphicsContext();
  const { timelineDomainInSeconds, timeLineContainerSize } =
    useHistoryReplayContext();

  const alarms = pointsQuery.data?.points.filter(
    (p) => p.type === PointType.Alarm,
  );

  const { data, isFetching } = useGraphicsHistoryQuery(
    {
      from: DateTime.fromISO(parsedParams.from!),
      to: DateTime.fromISO(parsedParams.to!),
      pointIds: alarms?.map((a) => a.id) || [],
    },
    { enabled: alarms && alarms.length > 0, staleTime: Infinity },
  );
  const alarmHistoryData = data?.history;

  const events = useMemo(
    () =>
      alarmHistoryData == undefined
        ? {}
        : getAlarmTimelineEvents(alarmHistoryData, alarms ?? []),
    [alarmHistoryData, alarms],
  );

  if (!alarmHistoryData || isFetching) {
    return null;
  }

  return (
    <AlarmEventScatters
      events={events}
      timelineDomainInSeconds={timelineDomainInSeconds}
      timeLineContainerSize={timeLineContainerSize}
    />
  );
}

type Props = {
  events: TimelineEvents;
} & Pick<
  HistoryReplayContextProps,
  "timelineDomainInSeconds" | "timeLineContainerSize"
>;
type ScatterData = {
  pixelX: number;
  clusterSize: number;
  point?: Point;
};

type AlarmProps = {
  point: ParsedPointId & Point;
  onClose: () => void;
  editMode: EditMode;
  placement?: Placement;
};

function AlarmEventScattersComponent({
  events,
  timelineDomainInSeconds,
  timeLineContainerSize,
}: Props) {
  const [focusAlarm, setFocusAlarm] = useState<AlarmProps | undefined>();
  const triggerRef = useRef<HTMLElement | null>(null);
  // Only use values that fit in the current (zoomed) timeline
  const values = Object.values(events)
    .flat()
    .filter(
      (v) =>
        v.timestampSeconds >= timelineDomainInSeconds[0] &&
        v.timestampSeconds <= timelineDomainInSeconds[1],
    );

  // Add some padding to our scatter icons. This ensures they appear and
  // disappear at correct positions in the timeline
  const padding =
    convertValueFromOldRangeToNewRange(
      SCATTER_SIZE / 2,
      [0, timeLineContainerSize.width],
      timelineDomainInSeconds,
    ) - timelineDomainInSeconds[0];

  // Cluster adjacent events to only show a single scatter with a number
  // indicating how many events are right next to each other
  const clusteredEvents = values.reduce((a, c) => {
    const posX = convertValueFromOldRangeToNewRange(
      c.timestampSeconds - padding,
      timelineDomainInSeconds,
      [0, timeLineContainerSize.width],
    );

    const adjacentTimestamp = a.find(
      (v) => Math.abs(v.pixelX - posX) <= SCATTER_SIZE / 2,
    );

    if (adjacentTimestamp) {
      adjacentTimestamp.clusterSize++;
    } else {
      a.push({ pixelX: posX, clusterSize: 1, point: c.point });
    }
    return a as ScatterData[];
  }, [] as ScatterData[]);

  const onMouseEnter = (
    e: React.MouseEvent<HTMLDivElement>,
    v: ScatterData,
  ) => {
    if (!v.point) {
      return;
    }

    triggerRef.current = e.currentTarget;
    setFocusAlarm({
      editMode: "tooltip",
      onClose: () => setFocusAlarm(undefined),
      point: v.point,
      placement: "top",
    });
  };

  const onMouseClick = () => {
    if (!focusAlarm) {
      return;
    }
    setFocusAlarm((prev) => ({
      placement: prev!.placement,
      onClose: prev!.onClose,
      point: prev!.point,
      editMode: "edit",
    }));
  };

  const onMouseLeave = () => {
    if (focusAlarm?.editMode === "edit") {
      return;
    }
    triggerRef.current = null;
    setFocusAlarm(undefined);
  };

  const scatters = clusteredEvents.map((v) => {
    return (
      <div
        key={v.pixelX}
        style={{
          position: "relative",
          left: v.pixelX,
          bottom: 64 + SCATTER_SIZE / 2,
        }}
      >
        <AlarmScatter>
          {v.clusterSize === 1 ? (
            <IconWrapper
              onMouseEnter={(e) => onMouseEnter(e, v)}
              onMouseLeave={onMouseLeave}
              onClick={onMouseClick}
              role="button"
            >
              <Icon icon="bell" size={18} />
            </IconWrapper>
          ) : (
            <Text variant="bodyExtraSmall" color="white">
              {v.clusterSize > 9 ? "9+" : v.clusterSize}
            </Text>
          )}
        </AlarmScatter>
      </div>
    );
  });

  return (
    <>
      {focusAlarm && (
        <SetValuePopover
          editMode={focusAlarm.editMode}
          onClose={focusAlarm.onClose}
          point={focusAlarm.point}
          placement={focusAlarm.placement}
          triggerRef={triggerRef}
        />
      )}
      {scatters}
    </>
  );
}

const AlarmEventScatters = memo(AlarmEventScattersComponent, arePropsEqual);

function arePropsEqual(oldProps: Props, newProps: Props) {
  if (!isEqual(oldProps.events, newProps.events)) {
    return false;
  }
  if (
    !isEqual(oldProps.timelineDomainInSeconds, newProps.timelineDomainInSeconds)
  ) {
    return false;
  }
  return (
    oldProps.timeLineContainerSize.width ===
    newProps.timeLineContainerSize.width
  );
}

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: all !important;
  z-index: 1;
  cursor: pointer;
  border-radius: 50%;
  &:hover {
    outline: 3px solid ${(p) => p.theme.colors.errorLight};
  }
`;

const AlarmScatter = styled.div`
  position: absolute;
  color: ${(p) => p.theme.colors.white};
  width: ${SCATTER_SIZE}px;
  height: ${SCATTER_SIZE}px;
  background: ${(p) => p.theme.colors.error};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
